import { changeUserPassword, getCurrentUserDetail } from '@/services/ant-design-pro/api';
import { encryptLoginPassword } from '@/utils/crypto';
import {
  PASSWORD_COMPLEXITY_HINT,
  requiredNewPasswordRulesWithOldCheck,
} from '@/utils/passwordValidation';
import { getUserInfo, handleLogout } from '@/utils/token';
import { Button, Form, Input, Modal, Space, App } from 'antd';
import React, { useCallback, useState } from 'react';
import { useModel } from '@umijs/max';
import { getInitialState } from '@/getInitialState';

export const ForceInitialPasswordModal: React.FC = () => {
  const { message } = App.useApp();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [form] = Form.useForm<{ oldPassword: string; newPassword: string; confirmPassword: string }>();
  const [submitting, setSubmitting] = useState(false);

  const open = initialState?.mustChangeInitialPassword === true;

  const refreshInitialState = useCallback(async () => {
    const next = await getInitialState();
    await setInitialState(next);
  }, [setInitialState]);

  const onFinish = async (values: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const user = getUserInfo() as { id?: number; username?: string } | null;
    if (!user?.id || !user.username) {
      message.error('用户信息异常，请重新登录');
      return;
    }

    setSubmitting(true);
    try {
      const encNew = encryptLoginPassword(values.newPassword);
      const encOld = encryptLoginPassword(values.oldPassword);
      const passwordResponse = await changeUserPassword({
        newPassword: encNew.encryptedPassword,
        newPasswordIv: encNew.iv,
        oldPassword: encOld.encryptedPassword,
        oldPasswordIv: encOld.iv,
        userId: user.id,
        username: user.username,
      });

      if (!passwordResponse?.success) {
        message.error(passwordResponse?.message || '密码修改失败');
        return;
      }

      message.success('密码修改成功');

      const detail = await getCurrentUserDetail();
      const prev = getUserInfo() as Record<string, unknown> | null;
      if (detail?.success && detail.result) {
        const merged = {
          ...(prev || {}),
          ...detail.result,
          needChangePassword: detail.result.needChangePassword ?? false,
          initialPasswordUnchanged: detail.result.initialPasswordUnchanged ?? false,
        };
        localStorage.setItem('jianyin_user_info', JSON.stringify(merged));
      } else if (prev) {
        localStorage.setItem(
          'jianyin_user_info',
          JSON.stringify({
            ...prev,
            needChangePassword: false,
            initialPasswordUnchanged: false,
          }),
        );
      }

      await refreshInitialState();
    } catch (e) {
      console.error(e);
      message.error('密码修改失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="请修改初始密码"
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={480}
      destroyOnClose
    >
      <p style={{ marginBottom: 16, color: 'rgba(0,0,0,0.65)' }}>
        为保障账户安全，首次登录须先修改初始密码后才能继续使用系统。
      </p>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark
      >
        <Form.Item
          label="当前密码"
          name="oldPassword"
          rules={[{ required: true, message: '请输入当前密码' }]}
        >
          <Input.Password autoComplete="current-password" placeholder="请输入当前登录密码" />
        </Form.Item>
        <Form.Item
          label="新密码"
          name="newPassword"
          dependencies={['oldPassword']}
          rules={requiredNewPasswordRulesWithOldCheck}
          extra={PASSWORD_COMPLEXITY_HINT}
        >
          <Input.Password autoComplete="new-password" placeholder={PASSWORD_COMPLEXITY_HINT} />
        </Form.Item>
        <Form.Item
          label="确认新密码"
          name="confirmPassword"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: '请再次输入新密码' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('两次输入的密码不一致'));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" placeholder="再次输入新密码" />
        </Form.Item>
        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => handleLogout(true)} disabled={submitting}>
              退出登录
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              确认修改
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};
